<template>
  <div class="download-page-wrapper">
    <!-- WeChat Browser Guide Overlay -->
    <div v-if="isWeChat" class="wechat-guide-overlay">
      <div class="wechat-guide-content">
        <div class="wechat-guide-arrow">
          <b-icon icon="arrow-up-right" font-scale="2" class="text-warning"></b-icon>
        </div>
        <div class="wechat-guide-text">
          <h5>微信内无法直接下载 APK</h5>
          <p>请点击右上角 <strong>「···」</strong> 菜单</p>
          <p>选择 <strong>「在浏览器打开」</strong> 即可立即下载</p>
        </div>
      </div>
    </div>

    <!-- WeChat Sticky Top Notice -->
    <div v-if="isWeChat" class="wechat-top-banner py-2 px-3">
      <div class="container d-flex justify-content-between align-items-center">
        <div class="d-flex align-items-center small text-white">
          <b-icon icon="info-circle-fill" class="mr-2 text-warning"></b-icon>
          <span>检测到微信环境：请点击右上角 <strong>「···」</strong> 选择 <strong>「在浏览器打开」</strong></span>
        </div>
      </div>
    </div>

    <div class="container py-4 download-container">
      <!-- Top Breadcrumb & Actions -->
      <div class="d-flex justify-content-between align-items-center mb-3">
        <router-link to="/home" class="btn btn-sm btn-outline-secondary d-inline-flex align-items-center back-home-btn">
          <b-icon icon="arrow-left" class="mr-1"></b-icon> 返回首页
        </router-link>
        <div class="text-muted small">
          官方正版 · 纯净安全 · 免费下载
        </div>
      </div>

      <!-- Hero Section -->
      <div class="card shadow-sm hero-card border-0 mb-4 overflow-hidden">
        <div class="hero-bg-accent"></div>
        <div class="card-body p-4 p-md-5 position-relative">
          <div class="row align-items-center">
            <!-- Left Info -->
            <div class="col-lg-7 mb-4 mb-lg-0 text-center text-lg-left">
              <div class="d-inline-flex align-items-center mb-3">
                <img :src="logoImg" alt="自在量化 Logo" class="app-hero-logo shadow mr-3" />
                <div>
                  <h1 class="h2 font-weight-bold mb-0 app-hero-title">自在量化</h1>
                  <span class="badge badge-primary px-2 py-1 mt-1 font-weight-normal">
                    <b-icon icon="phone" class="mr-1"></b-icon>Android 客户端
                  </span>
                </div>
              </div>

              <p class="lead app-hero-tagline mb-3 font-weight-bold">
                随时随地 看盘复盘 · 情绪量化 · 题材推演
              </p>
              <p class="app-hero-desc mb-4">
                专为手机端优化的高性能量化看盘客户端。支持情绪分时盯盘、题材推演、涨停晋级阶梯与自适应暗黑护眼模式，助你快速捕捉市场主线。
              </p>

              <!-- Version Meta Badges -->
              <div class="d-flex flex-wrap align-items-center justify-content-center justify-content-lg-start mb-4 version-meta-badges">
                <span class="meta-tag mr-2 mb-2">
                  <b-icon icon="tag" class="mr-1 text-primary"></b-icon>版本：v{{ versionInfo.version }}
                </span>
                <span class="meta-tag mr-2 mb-2">
                  <b-icon icon="hdd" class="mr-1 text-primary"></b-icon>大小：{{ versionInfo.fileSize }}
                </span>
                <span class="meta-tag mr-2 mb-2">
                  <b-icon icon="shield-check" class="mr-1 text-success"></b-icon>系统：{{ versionInfo.minAndroidVersion }}
                </span>
                <span class="meta-tag mb-2">
                  <b-icon icon="clock-history" class="mr-1 text-primary"></b-icon>更新：{{ versionInfo.releaseDate }}
                </span>
              </div>

              <!-- Action Buttons -->
              <div class="d-flex flex-wrap align-items-center justify-content-center justify-content-lg-start">
                <b-button
                  variant="primary"
                  size="lg"
                  class="font-weight-bold px-4 py-3 mr-3 mb-3 download-cta-btn shadow"
                  :href="downloadUrl"
                  download="zzapp-release-latest.apk"
                  @click="onDownloadClick"
                >
                  <b-icon icon="cloud-arrow-down-fill" class="mr-2"></b-icon>立即下载 Android APK
                </b-button>
                <a href="#changelog" class="btn btn-outline-secondary btn-lg px-3 py-3 mb-3 changelog-anchor-btn">
                  <b-icon icon="journal-text" class="mr-1"></b-icon>更新日志
                </a>
              </div>
            </div>

            <!-- Right QR Code for PC -->
            <div class="col-lg-5 text-center">
              <div class="qr-card p-4 rounded shadow-sm d-inline-block">
                <div class="qr-code-frame mb-3">
                  <img v-if="qrCodeDataUrl" :src="qrCodeDataUrl" alt="下载二维码" class="qr-code-image img-fluid" />
                  <div v-else class="qr-loading-box d-flex flex-column align-items-center justify-content-center">
                    <b-spinner variant="primary"></b-spinner>
                    <span class="small text-muted mt-2">生成二维码...</span>
                  </div>
                </div>
                <div class="qr-title font-weight-bold mb-1">
                  <b-icon icon="qr-code-scan" class="mr-1 text-primary"></b-icon>手机扫码直接下载
                </div>
                <div class="qr-desc small text-muted">
                  使用手机相机或浏览器扫码直达
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Features Grid Section -->
      <div class="section-title-wrap text-center mb-4">
        <h3 class="font-weight-bold section-main-title">核心功能特色</h3>
        <p class="text-muted small">集情绪周期、盘中量化、题材推演于一体的专业移动投研工具</p>
      </div>

      <div class="row mb-5">
        <div class="col-md-6 col-lg-3 mb-4">
          <div class="card h-100 feature-card shadow-sm border-0 p-3 text-center">
            <div class="feature-icon-box mx-auto mb-3 bg-primary-soft text-primary">
              <b-icon icon="graph-up" font-scale="1.8"></b-icon>
            </div>
            <h5 class="font-weight-bold feature-card-title mb-2">市场情绪周期</h5>
            <p class="feature-card-desc text-muted small mb-0">
              全市场盘中分时情绪监控，主情绪与人气K线直观对比，精准捕捉空间板与情绪冰点转折。
            </p>
          </div>
        </div>

        <div class="col-md-6 col-lg-3 mb-4">
          <div class="card h-100 feature-card shadow-sm border-0 p-3 text-center">
            <div class="feature-icon-box mx-auto mb-3 bg-success-soft text-success">
              <b-icon icon="diagram-3" font-scale="1.8"></b-icon>
            </div>
            <h5 class="font-weight-bold feature-card-title mb-2">题材推演与晋级</h5>
            <p class="feature-card-desc text-muted small mb-0">
              涨停梯队实时分层，题材演进路径追踪，结合龙虎榜顶级游资动向挖掘主线核心龙头。
            </p>
          </div>
        </div>

        <div class="col-md-6 col-lg-3 mb-4">
          <div class="card h-100 feature-card shadow-sm border-0 p-3 text-center">
            <div class="feature-icon-box mx-auto mb-3 bg-warning-soft text-warning">
              <b-icon icon="moon" font-scale="1.8"></b-icon>
            </div>
            <h5 class="font-weight-bold feature-card-title mb-2">自适应暗黑护眼</h5>
            <p class="feature-card-desc text-muted small mb-0">
              专为长时间盯盘打造的深色护眼模式，支持随系统自动切换或手动设置，启动极速省电。
            </p>
          </div>
        </div>

        <div class="col-md-6 col-lg-3 mb-4">
          <div class="card h-100 feature-card shadow-sm border-0 p-3 text-center">
            <div class="feature-icon-box mx-auto mb-3 bg-info-soft text-info">
              <b-icon icon="phone" font-scale="1.8"></b-icon>
            </div>
            <h5 class="font-weight-bold feature-card-title mb-2">移动随行专属优化</h5>
            <p class="feature-card-desc text-muted small mb-0">
              专为触屏与单手持机优化交互，原生级手势与流畅动画，告别网页端频繁缩放烦恼。
            </p>
          </div>
        </div>
      </div>

      <!-- Changelog Section -->
      <div id="changelog" class="row mb-5">
        <div class="col-lg-8 mx-auto">
          <div class="card shadow-sm changelog-card border-0">
            <div class="card-header bg-transparent border-bottom d-flex align-items-center py-3">
              <b-icon icon="card-checklist" class="mr-2 text-primary font-scale-1.2"></b-icon>
              <h5 class="mb-0 font-weight-bold changelog-header-title">版本更新说明</h5>
            </div>
            <div class="card-body p-4">
              <!-- v1.2.0 Latest -->
              <div class="changelog-item mb-4 pb-3 border-bottom">
                <div class="d-flex justify-content-between align-items-center mb-2 flex-wrap">
                  <div class="d-flex align-items-center">
                    <span class="badge badge-primary px-2 py-1 mr-2">v{{ versionInfo.version }} 最新发布</span>
                    <span class="font-weight-bold version-title">暗黑模式升级 & 稳定性强化</span>
                  </div>
                  <span class="text-muted small">{{ versionInfo.releaseDate }}</span>
                </div>
                <ul class="changelog-list mb-0 pl-3 small">
                  <li v-for="(item, idx) in versionInfo.changelog" :key="idx" class="mb-1">
                    {{ item }}
                  </li>
                </ul>
              </div>

              <!-- Historical Versions -->
              <div class="changelog-item">
                <div class="d-flex justify-content-between align-items-center mb-2 flex-wrap">
                  <div class="d-flex align-items-center">
                    <span class="badge badge-secondary px-2 py-1 mr-2">v1.1.0</span>
                    <span class="font-weight-bold version-title">移动端全功能正式上线</span>
                  </div>
                  <span class="text-muted small">2026.08.15</span>
                </div>
                <ul class="changelog-list mb-0 pl-3 small text-muted">
                  <li class="mb-1">支持情绪分时监控、涨停梯队与龙虎榜看板</li>
                  <li class="mb-1">集成易筋经热点推演与题材挖掘模块</li>
                  <li class="mb-1">优化移动网络加载延迟与数据缓存性能</li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Installation Guide & FAQ -->
      <div class="row mb-5">
        <div class="col-lg-8 mx-auto">
          <div class="card shadow-sm faq-card border-0">
            <div class="card-header bg-transparent border-bottom d-flex align-items-center py-3">
              <b-icon icon="question-circle" class="mr-2 text-primary font-scale-1.2"></b-icon>
              <h5 class="mb-0 font-weight-bold faq-header-title">安装与使用常见问题</h5>
            </div>
            <div class="card-body p-4">
              <div class="faq-item mb-3">
                <h6 class="font-weight-bold faq-question mb-1">
                  <b-icon icon="patch-question" class="mr-1 text-primary"></b-icon>1. 安装时手机提示“未知来源应用”或“安全风险”怎么办？
                </h6>
                <p class="small text-muted mb-0 pl-3">
                  本客户端为官方打包发布的自主签名安装包，安全无毒无广告。部分 Android 手机（如华为、小米、OPPO、vivo）会在首次安装非应用商店应用时进行安全提示，请放心勾选“允许来自此来源的应用”或选择“继续安装/忽略风险”即可顺利完成安装。
                </p>
              </div>
              <hr />
              <div class="faq-item mb-3">
                <h6 class="font-weight-bold faq-question mb-1">
                  <b-icon icon="patch-question" class="mr-1 text-primary"></b-icon>2. 微信内扫描二维码或点击下载无反应？
                </h6>
                <p class="small text-muted mb-0 pl-3">
                  微信内置浏览器默认会限制直接下载 APK 文件。请点击微信右上角的 <strong>「···」</strong> 按钮，在弹出菜单中选择 <strong>「在浏览器中打开」</strong>（推荐使用系统自带浏览器、Chrome 或 Edge），即可正常弹出下载任务。
                </p>
              </div>
              <hr />
              <div class="faq-item">
                <h6 class="font-weight-bold faq-question mb-1">
                  <b-icon icon="patch-question" class="mr-1 text-primary"></b-icon>3. 是否支持 iPhone / iPad 苹果设备？
                </h6>
                <p class="small text-muted mb-0 pl-3">
                  当前安装包为 Android 专属 APK。iOS 用户建议在 iPhone / iPad 上使用 Safari 浏览器访问本站，并点击底部分享按钮选择 <strong>「添加到主屏幕」</strong>，同样可获得类似原生 App 的全屏流畅体验。
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Bottom CTA Banner -->
      <div class="card bottom-cta-card shadow-sm border-0 text-center p-4 p-md-5">
        <h4 class="font-weight-bold mb-2">立即下载，开启高效看盘体验</h4>
        <p class="text-muted small mb-4">掌握盘中实时情绪周期，不错过任何主线题材演变</p>
        <div class="d-flex justify-content-center">
          <b-button
            variant="primary"
            size="lg"
            class="font-weight-bold px-4 py-2 shadow"
            :href="downloadUrl"
            download="zzapp-release-latest.apk"
            @click="onDownloadClick"
          >
            <b-icon icon="download" class="mr-2"></b-icon>下载最新 APK (v{{ versionInfo.version }})
          </b-button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import QRCode from 'qrcode'

export default {
  name: 'Download',
  data() {
    return {
      logoImg: require('@/assets/img/logo.png'),
      qrCodeDataUrl: '',
      isWeChat: false,
      versionInfo: {
        appName: '自在量化',
        version: '1.2.0',
        releaseDate: '2026-09-17',
        fileSize: '12.2 MB',
        minAndroidVersion: 'Android 8.0+',
        downloadPath: '/downloads/zzapp-release-latest.apk',
        changelog: [
          '全新支持深色/浅色模式无缝切换与首次启动引导',
          '强化情绪周期、盘中分时与全市场涨跌统计',
          '题材推演与涨停复盘核心逻辑演进追踪',
          '优化移动端离线缓存与极端网络下的自愈重连'
        ]
      }
    }
  },
  computed: {
    downloadUrl() {
      if (typeof window !== 'undefined') {
        return window.location.origin + this.versionInfo.downloadPath
      }
      return this.versionInfo.downloadPath
    },
    qrCodeUrl() {
      if (typeof window !== 'undefined') {
        return window.location.origin + '/download.html'
      }
      return '/download.html'
    }
  },
  mounted() {
    this.checkWeChat()
    this.generateQrCode()
    this.fetchVersionInfo()
  },
  methods: {
    checkWeChat() {
      if (typeof navigator !== 'undefined') {
        const ua = navigator.userAgent.toLowerCase()
        this.isWeChat = ua.indexOf('micromessenger') !== -1
      }
    },
    async generateQrCode() {
      try {
        const targetUrl = this.qrCodeUrl
        const url = await QRCode.toDataURL(targetUrl, {
          width: 220,
          margin: 1,
          color: {
            dark: '#0f172a',
            light: '#ffffff'
          }
        })
        this.qrCodeDataUrl = url
      } catch (err) {
        console.error('Failed to generate QR code for download page:', err)
      }
    },
    async fetchVersionInfo() {
      try {
        const res = await fetch('/downloads/version.json')
        if (res.ok) {
          const data = await res.json()
          if (data && data.version) {
            this.versionInfo = Object.assign({}, this.versionInfo, data)
          }
        }
      } catch (e) {
        // Fallback to initial data
      }
    },
    onDownloadClick() {
      if (this.isWeChat) {
        this.$bvToast.toast('微信环境限制直接下载 APK，请点击右上角「···」选择「在浏览器打开」', {
          title: '微信环境提醒',
          variant: 'warning',
          solid: true,
          autoHideDelay: 5000
        })
      }
    }
  }
}
</script>

<style scoped>
.download-page-wrapper {
  background-color: var(--bg-main, #f8fafc);
  min-height: calc(100vh - 60px);
}

.download-container {
  max-width: 1140px;
}

/* WeChat top banner */
.wechat-top-banner {
  background: linear-gradient(90deg, #10b981, #059669);
}

/* WeChat overlay guide */
.wechat-guide-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100vw;
  height: 100vh;
  background: rgba(0, 0, 0, 0.85);
  z-index: 99999;
  display: flex;
  justify-content: center;
  align-items: flex-start;
  padding-top: 20px;
}

.wechat-guide-content {
  text-align: right;
  width: 90%;
  max-width: 400px;
  color: #ffffff;
}

.wechat-guide-arrow {
  animation: pulseArrow 1.2s infinite alternate ease-in-out;
  margin-right: 15px;
}

@keyframes pulseArrow {
  0% { transform: translate(0, 0); }
  100% { transform: translate(8px, -8px); }
}

.wechat-guide-text {
  text-align: center;
  margin-top: 20px;
  background: rgba(255, 255, 255, 0.1);
  backdrop-filter: blur(10px);
  padding: 20px;
  border-radius: 12px;
  border: 1px solid rgba(255, 255, 255, 0.2);
}

/* Hero Section */
.hero-card {
  background-color: var(--bg-card, #ffffff);
  border-radius: 16px;
  position: relative;
}

.hero-bg-accent {
  position: absolute;
  top: -100px;
  right: -100px;
  width: 300px;
  height: 300px;
  background: radial-gradient(circle, rgba(59, 130, 246, 0.12) 0%, rgba(59, 130, 246, 0) 70%);
  pointer-events: none;
}

.app-hero-logo {
  width: 68px;
  height: 68px;
  border-radius: 16px;
  object-fit: cover;
}

.app-hero-title {
  color: var(--text-main, #1e293b);
}

.app-hero-tagline {
  color: var(--accent-color, #3b82f6);
  letter-spacing: 0.5px;
}

.app-hero-desc {
  color: var(--text-muted, #64748b);
  line-height: 1.7;
}

.meta-tag {
  background-color: var(--bg-muted, #f1f5f9);
  color: var(--text-main, #1e293b);
  border-radius: 8px;
  padding: 4px 10px;
  font-size: 0.85rem;
  font-weight: 500;
  border: 1px solid var(--border-color, #e2e8f0);
}

.download-cta-btn {
  border-radius: 10px;
  letter-spacing: 0.5px;
  transition: transform 0.15s ease, box-shadow 0.15s ease;
}

.download-cta-btn:hover {
  transform: translateY(-2px);
  box-shadow: 0 6px 16px rgba(59, 130, 246, 0.35) !important;
}

.changelog-anchor-btn {
  border-radius: 10px;
}

/* QR Code Card */
.qr-card {
  background-color: var(--bg-muted, #f8fafc);
  border: 1px solid var(--border-color, #e2e8f0);
  max-width: 280px;
}

.qr-code-frame {
  background-color: #ffffff;
  padding: 10px;
  border-radius: 12px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08);
}

.qr-code-image {
  width: 190px;
  height: 190px;
  display: block;
}

.qr-loading-box {
  width: 190px;
  height: 190px;
}

.qr-title {
  color: var(--text-main, #1e293b);
}

/* Features Grid */
.section-main-title {
  color: var(--text-main, #1e293b);
}

.feature-card {
  background-color: var(--bg-card, #ffffff);
  border-radius: 14px;
  transition: transform 0.2s ease, box-shadow 0.2s ease;
}

.feature-card:hover {
  transform: translateY(-4px);
  box-shadow: 0 8px 20px rgba(0, 0, 0, 0.08) !important;
}

.feature-icon-box {
  width: 60px;
  height: 60px;
  border-radius: 16px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.bg-primary-soft { background-color: rgba(59, 130, 246, 0.1); }
.bg-success-soft { background-color: rgba(34, 197, 94, 0.1); }
.bg-warning-soft { background-color: rgba(245, 158, 11, 0.1); }
.bg-info-soft { background-color: rgba(6, 182, 212, 0.1); }

.feature-card-title {
  color: var(--text-main, #1e293b);
}

.feature-card-desc {
  line-height: 1.6;
}

/* Changelog Card */
.changelog-card,
.faq-card,
.bottom-cta-card {
  background-color: var(--bg-card, #ffffff);
  border-radius: 16px;
}

.changelog-header-title,
.faq-header-title {
  color: var(--text-main, #1e293b);
}

.version-title {
  color: var(--text-main, #1e293b);
}

.changelog-list {
  color: var(--text-muted, #64748b);
  line-height: 1.8;
}

.faq-question {
  color: var(--text-main, #1e293b);
}

/* Dark Mode Support */
body.theme-dark .meta-tag,
.theme-dark .meta-tag {
  background-color: var(--bg-muted, #334155);
  color: var(--text-main, #f1f5f9);
  border-color: var(--border-color, #334155);
}

body.theme-dark .qr-card,
.theme-dark .qr-card {
  background-color: var(--bg-muted, #1e293b);
  border-color: var(--border-color, #334155);
}

body.theme-dark .feature-card,
.theme-dark .feature-card,
body.theme-dark .hero-card,
.theme-dark .hero-card,
body.theme-dark .changelog-card,
.theme-dark .changelog-card,
body.theme-dark .faq-card,
.theme-dark .faq-card,
body.theme-dark .bottom-cta-card,
.theme-dark .bottom-cta-card {
  background-color: var(--bg-card, #1e293b);
  border-color: var(--border-color, #334155);
}

body.theme-dark .changelog-item.border-bottom,
.theme-dark .changelog-item.border-bottom {
  border-color: var(--border-color, #334155) !important;
}

body.theme-dark hr,
.theme-dark hr {
  border-color: var(--border-color, #334155);
}

@media (max-width: 768px) {
  .hero-card .card-body {
    padding: 1.5rem !important;
  }
  .app-hero-title {
    font-size: 1.4rem;
  }
  .app-hero-tagline {
    font-size: 1.05rem;
  }
}
</style>
